/**
 * Die Figureninformation — §51.
 *
 * §51 nennt sechs Angaben: Figurenname, Besitzer, Team, Bewegung, Schlagreichweite und
 * Sonderfähigkeit. Hier entstehen sie aus der **RuleConfig dieser Partie** (`state.ruleConfig`),
 * nie aus der globalen Konfiguration — §53 und `CLAUDE.md` Regel 3. Das ist nicht Formsache: wer
 * im Balancing die Reichweite des Wächters ändert, soll sie in **seiner** Partie sehen, und zwar
 * die, mit der sie wirklich gespielt wird.
 *
 * **Gelesen wird über die Engine**, nicht über das Config-Objekt: `captureRange` löst den Marker
 * `'sameAsMovement'` auf (Läufer, Springer), `pushConfig` und `canJump` lesen generisch. Ein
 * eigener Zweig hier wäre eine zweite Stelle, die bei jeder Regeländerung nachzuziehen wäre —
 * und `pieces.ts` sagt ausdrücklich, dass Regelcode Werte nur über diese Funktionen liest.
 *
 * **`capture: undefined` ist eine Regel, kein fehlender Wert.** Der Fähnrich schlägt nur den
 * gegnerischen Fähnrich auf dem Zielfeld (§14, Nr. 18), der Stoßer stößt statt zu schlagen
 * (§18). „Keine Schlagreichweite" ist etwas anderes als „Schlagreichweite 0", und die Anzeige
 * muss beides auseinanderhalten können.
 *
 * **Die Figur kann auf dem Brett oder im Vorrat liegen.** Die Angaben hängen an der **Klasse**,
 * nicht an der Position — eine geschlagene Figur, die auf ihren Respawn wartet, hat dieselben
 * Werte.
 */

import {
  allPieces,
  canJump,
  capturedOf,
  captureRange,
  movementRange,
  playerOf,
  pushConfig,
  type GameState,
  type PieceType,
  type SectorId,
} from '@flaghex/engine';

export interface PieceRange {
  readonly min: number;
  /** `null` heißt **unbegrenzt** — der Default des Läufers. */
  readonly max: number | null;
}

export interface PiecePush {
  readonly range: { readonly min: number; readonly max: number };
  readonly distance: number;
  readonly pusherAdvance: number;
  readonly canPushOffBoard: boolean;
  /** Den Fähnrich aus dem Zielfeld stoßen (`R11`, Nr. 51). */
  readonly canPushFlagBearerFromGoal: boolean;
}

export interface PieceInfo {
  readonly type: PieceType;
  readonly owner: string;
  readonly sector: SectorId;
  readonly team: string | undefined;
  readonly movement: PieceRange;
  /** `undefined`, wenn die Figur kein normales CAPTURE ausführt (§14, §18). */
  readonly capture: PieceRange | undefined;
  /** Nur beim Stoßer — und auch dort nur, solange die RuleConfig es so hält. */
  readonly push: PiecePush | undefined;
  readonly canJump: boolean;
  /** Der Fähnrich: betritt als einzige Figur das Zielfeld und punktet dort (§19, §23). */
  readonly flagBearer: boolean;
}

export function pieceInfoOf(state: GameState, pieceId: string): PieceInfo | undefined {
  const placed = allPieces(state.boardState).find((piece) => piece.id === pieceId);
  const pooled = state.players
    .flatMap((player) => capturedOf(state.boardState, player.id))
    .find((piece) => piece.id === pieceId);
  const piece = placed ?? pooled;
  if (piece === undefined) {
    return undefined;
  }

  const config = state.ruleConfig;
  const player = playerOf(state, piece.owner);

  return {
    type: piece.type,
    owner: piece.owner,
    sector: (player?.sector ?? 'green') as SectorId,
    team: player?.team,
    movement: movementRange(config, piece.type),
    capture: captureRange(config, piece.type),
    push: pushConfig(config, piece.type),
    canJump: canJump(config, piece.type),
    flagBearer: piece.type === 'flagBearer',
  };
}
